/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { mapReplacer, mapReviver } from "./util"

type StoredAccessTokenData = {
  value: string
  expiresAt: Date
  userId: string
}

/**
 * This class handles the local storage of access tokens. It is effectively a queue, which stores a fixed
 *  number of access tokens (FIFO). We store these tokens so the user doesn't have to request a new one every time
 *   they access the page.
 */
class AccessTokenStorage {
  private readonly _storageKey: string
  private readonly _store: Storage

  // `store` defaults to localStorage (legacy flows). Pass sessionStorage for tokens that
  // should be tab-scoped and die with the tab (e.g. the anonymous D2C booking session).
  constructor(storageKey: string, store: Storage = localStorage) {
    this._storageKey = storageKey
    this._store = store
  }

  private static readonly _maxSize = 20

  get(key: string): StoredAccessTokenData | null {
    const data = this._store.getItem(this._storageKey)
    if (!data) {
      return null
    }
    const tokens = JSON.parse(data, mapReviver) as Map<string, StoredAccessTokenData>
    return tokens.get(key) ?? null
  }

  set(key: string, accessTokenData: StoredAccessTokenData): void {
    const data = this._store.getItem(this._storageKey)
    const tokens: Map<string, StoredAccessTokenData> = data
      ? (JSON.parse(data, mapReviver) as Map<string, StoredAccessTokenData>)
      : new Map<string, StoredAccessTokenData>()
    // Cap at 20 (FIFO): when adding a NEW key would exceed the cap, evict the oldest. A Map keeps
    // insertion order, so the oldest key is the first the iterator yields — take it via `.next()`.
    // (`keys()` is an iterator, not an array: `keys()[n]` is always `undefined`, so the old index
    // access deleted nothing and the store grew without bound.)
    if (!tokens.has(key) && tokens.size >= AccessTokenStorage._maxSize) {
      const oldestKey = tokens.keys().next().value as string
      tokens.delete(oldestKey)
    }
    tokens.set(key, accessTokenData)
    this._store.setItem(this._storageKey, JSON.stringify(tokens, mapReplacer))
  }

  remove(key: string): void {
    const data = this._store.getItem(this._storageKey)
    if (!data) {
      return
    }
    const tokens = JSON.parse(data, mapReviver) as Map<string, StoredAccessTokenData>
    if (!tokens.delete(key)) {
      return
    }
    // Drop the whole key when the last token goes, so the empty state matches the
    // `!data` fast-path instead of leaving a stale empty map behind.
    if (tokens.size === 0) {
      this._store.removeItem(this._storageKey)
    } else {
      this._store.setItem(this._storageKey, JSON.stringify(tokens, mapReplacer))
    }
  }
}

export default AccessTokenStorage
